import React, { useEffect, useState } from 'react';
import { getCategories, addCategory, updateCategory, deleteCategory, reorderCategories } from '../services/categoryService';
import { hasPermission } from '../services/authService';
import { resolveAssetUrl } from '../utils/resolveUrl';
import './Admin.css';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableCategoryItem({ cat, depth, onEdit, onAddSub, onDelete, resolveAssetUrl, onReorder, highlightedId }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: cat.id });

  const isHighlighted = highlightedId === cat.id;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginBottom: 8,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 2 : 1, // Higher z-index for dragging
    position: 'relative'
  };

  const hasSubcategories = Array.isArray(cat.subcategories) && cat.subcategories.length > 0;



  return (
    <div ref={setNodeRef} style={style}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '12px 16px',
        background: isHighlighted ? '#f0f9ff' : '#fff',
        border: isHighlighted ? '1px solid #0ea5e9' : '1px solid #e2e8f0',
        borderRadius: 8,
        marginLeft: depth * 32,
        position: 'relative',
        boxShadow: isDragging ? '0 8px 16px rgba(0,0,0,0.15)' : (isHighlighted ? '0 0 0 2px rgba(14, 165, 233, 0.2)' : 'none'),
        transition: 'background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease'
      }}>
        {/* Drag Handle */}
        <div {...attributes} {...listeners} style={{ cursor: 'grab', color: '#94a3b8', padding: '12px 12px 12px 0', marginLeft: -4 }}>
          ⠿
        </div>

        {/* Accordion Toggle */}
        {hasSubcategories ? (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748b',
              transition: 'transform 0.2s ease',
              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
            }}
          >
            ▶
          </button>
        ) : (
          <div style={{ width: 24 }} />
        )}

        {/* Connecting line for depth > 0 */}
        {depth > 0 && (
          <div style={{
            position: 'absolute',
            left: -20,
            top: '50%',
            width: 20,
            height: 1,
            background: '#cbd5e1'
          }} />
        )}

        <div style={{ width: 44, height: 44, background: '#f8fafc', borderRadius: 6, overflow: 'hidden', flexShrink: 0, border: '1px solid #f1f5f9' }}>
          {cat.image ? (
            <img src={resolveAssetUrl(cat.image)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 10 }}>IMG</div>
          )}
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>{cat.name}</div>
          {cat.description && <div style={{ fontSize: 12, color: '#64748b', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 300 }}>{cat.description}</div>}
        </div>

        <div style={{ display: 'flex', gap: 6 }}>
          <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => onEdit(cat)}>Edit</button>
          <button className="admin-btn admin-btn-secondary admin-btn-sm" onClick={() => onAddSub(cat)} title="Add subcategory">+ Sub</button>
          <button className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => onDelete(cat.id)}>Delete</button>
        </div>
      </div>

      {hasSubcategories && isExpanded && (
        <div style={{ marginTop: 8, position: 'relative' }}>
          <div style={{ position: 'absolute', left: (depth * 32) + 6, top: -10, bottom: 24, width: 1, background: '#cbd5e1' }} />
          <SortableCategoryList
            categories={cat.subcategories}
            parentId={cat.id}
            depth={depth + 1}
            onEdit={onEdit}
            onAddSub={onAddSub}
            onDelete={onDelete}
            resolveAssetUrl={resolveAssetUrl}
            onReorder={onReorder}
            highlightedId={highlightedId}
          />
        </div>
      )}
    </div>
  );
}

function SortableCategoryList({ categories, parentId, depth, onEdit, onAddSub, onDelete, resolveAssetUrl, onReorder, highlightedId }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = categories.findIndex(c => c.id === active.id);
      const newIndex = categories.findIndex(c => c.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = arrayMove(categories, oldIndex, newIndex);
        onReorder(newItems, parentId, active.id);
      }
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={categories.map(c => c.id)}
        strategy={verticalListSortingStrategy}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {categories.map(cat => (
            <SortableCategoryItem
              key={cat.id}
              cat={cat}
              depth={depth}
              onEdit={onEdit}
              onAddSub={onAddSub}
              onDelete={onDelete}
              resolveAssetUrl={resolveAssetUrl}
              onReorder={onReorder}
              highlightedId={highlightedId}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export default function CategoriesAdmin() {
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', imageFile: null, image: '', parentId: '' });
  const [editId, setEditId] = useState(null);
  const [highlightedId, setHighlightedId] = useState(null);
  const admin = hasPermission('admin.categories');

  const loadCategories = async () => {
    const cats = await getCategories(true);
    setCategories(cats);
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const openAdd = () => {
    setEditId(null);
    setForm({ name: '', description: '', imageFile: null, image: '', parentId: '' });
    setShowForm(true);
  };

  const openEdit = (cat) => {
    setEditId(cat.id);
    setForm({ name: cat.name || '', description: cat.description || '', imageFile: null, image: cat.image || '', parentId: cat.parentId || '' });
    setShowForm(true);
  };

  const openAddSub = (cat) => {
    setEditId(null);
    setForm({ name: '', description: '', imageFile: null, image: '', parentId: cat.id });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('name', form.name);
    fd.append('description', form.description);
    if (form.imageFile) fd.append('image', form.imageFile);
    if (form.parentId) fd.append('parentId', form.parentId);
    if (editId) await updateCategory(editId, fd); else await addCategory(fd);
    await loadCategories();
    setShowForm(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    await deleteCategory(id);
    await loadCategories();
  };

  const handleReorder = async (newList, parentId = null, movedId) => {
    // Update local state first for snappiness
    const updateRecursive = (list) => {
      // If we are updating roots
      if (parentId === null) return newList;

      return list.map(cat => {
        if (cat.id === parentId) {
          return { ...cat, subcategories: newList };
        }
        if (cat.subcategories) {
          return { ...cat, subcategories: updateRecursive(cat.subcategories) };
        }
        return cat;
      });
    };

    setCategories(prev => {
      if (parentId === null) return newList;
      return updateRecursive(prev);
    });

    // Handle highlighting
    if (movedId) {
      setHighlightedId(movedId);
      setTimeout(() => setHighlightedId(null), 3000);
    }

    // Send to backend
    const orders = newList.map((cat, index) => ({ id: cat.id, order: index + 1 }));
    await reorderCategories(orders);
  };

  if (!admin) return <div className="admin-text" style={{ padding: 20 }}>Admin access required.</div>;

  return (
    <div style={{ padding: 20 }}>
      {/* Inline styles for local animations if needed */}
      <style>{`
        .admin-category-highlight {
          animation: highlight-fade 3s forwards;
        }
        @keyframes highlight-fade {
          from { background-color: #f0f9ff; border-color: #0ea5e9; }
          to { background-color: #fff; border-color: #e2e8f0; }
        }
      `}</style>



      <div className="admin-card" style={{ padding: 0 }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="admin-btn admin-btn-primary" onClick={openAdd} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            New Category
          </button>
        </div>
        <div style={{ padding: '16px 20px' }}>
        {!categories.length ? (
          <div className="admin-text">No categories found.</div>
        ) : (
          <SortableCategoryList
            categories={categories}
            depth={0}
            onEdit={openEdit}
            onAddSub={openAddSub}
            onDelete={remove}
            resolveAssetUrl={resolveAssetUrl}
            onReorder={handleReorder}
            highlightedId={highlightedId}
          />
        )}
        </div>
      </div>


      {showForm && (
        <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', zIndex: 100 }}>
          <div className="admin-card" style={{ width: '90%', maxWidth: 600, margin: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="admin-title" style={{ fontSize: 18, margin: 0 }}>{editId ? 'Edit Category' : 'New Category'}</h3>
              <button aria-label="Close" onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#64748b' }}>&times;</button>
            </div>
            <form onSubmit={submit}>
              <div className="admin-form-group">
                <label className="admin-label">Category Name</label>
                <input className="admin-input" placeholder="e.g. Appliances" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="admin-form-group">
                <label className="admin-label">Description</label>
                <textarea className="admin-textarea" placeholder="Optional description..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
              </div>

              <div className="admin-form-group">
                <label className="admin-label">Parent Category</label>
                <select className="admin-select" value={form.parentId || ''} onChange={(e) => setForm({ ...form, parentId: e.target.value || '' })}>
                  <option value="">— Top Level —</option>
                  {categories.map(cat => {
                    // Prevent circular dependency (can't select self as parent)
                    if (cat.id === editId) return null;
                    const renderOpts = (node, prefix = '') => {
                      const opts = [];
                      opts.push(<option key={node.id} value={node.id} disabled={node.id === editId}>{prefix + node.name}</option>);
                      if (Array.isArray(node.subcategories) && node.subcategories.length) {
                        node.subcategories.forEach(child => opts.push(...renderOpts(child, prefix + '— ')));
                      }
                      return opts;
                    };
                    return renderOpts(cat);
                  })}
                </select>
              </div>

              <div className="admin-form-group">
                <label className="admin-label">Category Image</label>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <input className="admin-input" type="file" accept="image/*" onChange={(e) => setForm({ ...form, imageFile: e.target.files?.[0] || null })} style={{ padding: 8 }} />
                  {form.image && !form.imageFile && (
                    <img
                      src={resolveAssetUrl(form.image)}
                      alt=""
                      style={{ height: 48, borderRadius: 4, border: '1px solid #e2e8f0' }}
                    />
                  )}
                </div>
              </div>

              <div className="admin-actions">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="admin-btn admin-btn-primary">Save Category</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
